import { createContext, useContext, useReducer, useMemo } from "react";

const SettingsContext = createContext(null);

const initialSettingsState = {
  settings: {
    darkMode: false,
    privacyLock: false,
    hideSensitiveText: true,
    appPin: null, // Load from localStorage if available
  },
  privacy: {
    lockEnabled: false,
    isUnlocked: true,
    unlockedAt: null,
  },
};

// Ensure settings state is valid and has no null/undefined values
function validateSettingsState(state) {
  if (!state || typeof state !== 'object') {
    return { ...initialSettingsState };
  }
  return {
    settings: {
      darkMode: state?.settings?.darkMode === true,
      privacyLock: state?.settings?.privacyLock === true,
      hideSensitiveText: typeof state?.settings?.hideSensitiveText === 'boolean' 
        ? state.settings.hideSensitiveText 
        : true,
      appPin: typeof state?.settings?.appPin === 'string' ? state.settings.appPin : null,
    },
    privacy: {
      lockEnabled: state?.privacy?.lockEnabled === true,
      isUnlocked: state?.privacy?.isUnlocked !== false, // Default to true
      unlockedAt: state?.privacy?.unlockedAt ?? null,
    },
  };
}

function settingsReducer(state, action) {
  switch (action.type) {
    case "UPDATE_SETTINGS":
      return {
        ...state,
        settings: action.payload,
      };

    case "UNLOCK_APP_PRIVACY":
      return {
        ...state,
        privacy: {
          ...state.privacy,
          isUnlocked: true,
          unlockedAt: action.payload.unlockedAt,
        },
      };

    case "LOCK_APP_PRIVACY":
      return {
        ...state,
        privacy: {
          ...state.privacy,
          isUnlocked: false,
          unlockedAt: null,
        },
      };

    case "TOGGLE_PRIVACY_LOCK":
      return {
        ...state,
        settings: {
          ...state.settings,
          privacyLock: action.payload,
        },
        privacy: {
          ...state.privacy,
          isUnlocked: false,
          unlockedAt: null,
        },
      };

    case "HYDRATE_SETTINGS":
      return action.payload;

    default:
      return state;
  }
}

export function SettingsProvider({ children, initialState }) {
  // Initialize with PIN loaded from localStorage if available
  const getInitialState = () => {
    try {
      const savedPin = localStorage?.getItem?.("appPin");
      const baseState = initialState || initialSettingsState;
      const validated = validateSettingsState(baseState);
      return {
        ...validated,
        settings: {
          ...validated.settings,
          appPin: typeof savedPin === 'string' ? savedPin : null,
        },
      };
    } catch (error) {
      console.warn("Could not load settings from localStorage:", error);
      return validateSettingsState(initialSettingsState);
    }
  };

  const [state, dispatch] = useReducer(settingsReducer, undefined, getInitialState);

  // Memoize computed values
  const masked = useMemo(
    () => state.settings.hideSensitiveText,
    [state.settings.hideSensitiveText]
  );

  const privacyLocked = useMemo(
    () => state.settings.privacyLock && !state.privacy.isUnlocked,
    [state.settings.privacyLock, state.privacy.isUnlocked]
  );

  const actions = {
    setSetting: (key, value) => {
      dispatch({
        type: "UPDATE_SETTINGS",
        payload: {
          ...state.settings,
          [key]: value,
        },
      });
    },

    updateSettings: (settings) => {
      dispatch({
        type: "UPDATE_SETTINGS",
        payload: settings,
      });
    },

    unlockAppPrivacy: () => {
      dispatch({
        type: "UNLOCK_APP_PRIVACY",
        payload: { unlockedAt: Date.now() },
      });
    },

    lockAppPrivacy: () => {
      dispatch({
        type: "LOCK_APP_PRIVACY",
      });
    },

    togglePrivacyLock: (enabled) => {
      dispatch({
        type: "TOGGLE_PRIVACY_LOCK",
        payload: enabled,
      });
    },

    hydrateSettings: (savedState) => {
      dispatch({
        type: "HYDRATE_SETTINGS",
        payload: savedState,
      });
    },
  };

  // Memoize provider value - critical to prevent cascade re-renders
  const value = useMemo(
    () => ({
      state,
      actions,
      masked,
      privacyLocked,
    }),
    [state, masked, privacyLocked]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return context;
}
